import { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormHelperText, Grid, IconButton, InputLabel, MenuItem, Select, TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import PropTypes from 'prop-types';
import { useDispatch, useStore } from 'react-redux';

import { addProfileAddress } from '../../actions/profile';
import { isEmpty, puntoDeGoogle } from '../common/helpers';

const DIRECCION_VACIA = {
  direccion: '', calle: '', numeroExt: '', numeroInt: '', cp: '', colonia: '',
  municipio: '', nombre: '', estado: '', pais: '', referencia: '', place_id: '', location: null,
};
const REQUERIDOS = ['estado', 'municipio', 'colonia', 'calle', 'nombre', 'pais'];
// Google devuelve el país en inglés; el perfil lo guarda así
const PAISES = { Mexico: 'México', 'United States': 'EEUU' };

const Campo = ({ id, label, requerido, error, ...props }) => (
  <>
    <InputLabel htmlFor={id} required={requerido}>{label}</InputLabel>
    <TextField id={id} size="small" fullWidth autoComplete="off" error={!!error} helperText={error} {...props} />
  </>
);

// Agrega una dirección al perfil sin salir de donde estás (p. ej. el formulario de páginas).
// Al guardar llama onSaved(direccionNueva) para que quien lo abrió la seleccione.
const ModalDireccion = ({ open, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [data, setData] = useState(DIRECCION_VACIA);
  const [errores, setErrores] = useState({});
  const [lugar, setLugar] = useState(null); // opción elegida en el buscador de Google
  const [guardando, setGuardando] = useState(false);

  // Cada vez que se abre arranca limpio
  useEffect(() => {
    if (open) {
      setData(DIRECCION_VACIA);
      setErrores({});
      setLugar(null);
      setGuardando(false);
    }
  }, [open]);

  const cambiar = (campo, valor) => {
    setData(prev => ({ ...prev, [campo]: valor }));
    if (errores[campo]) setErrores(prev => ({ ...prev, [campo]: null }));
  };

  // Rellena los campos con lo que devuelve Google al elegir una dirección
  const elegirLugar = async (place) => {
    setLugar(place);
    const placeId = place?.value?.place_id;
    if (!placeId) return;
    try {
      const results = await geocodeByPlaceId(placeId);
      if (!results?.length) return;
      const det = {};
      results[0].address_components.forEach(({ types, long_name }) => {
        if (types.includes('street_number')) det.numeroExt = long_name;
        if (types.includes('route')) det.calle = long_name;
        if (types.includes('sublocality') || types.includes('neighborhood')) det.colonia = long_name;
        if (types.includes('locality')) det.municipio = long_name;
        if (types.includes('administrative_area_level_1')) det.estado = long_name;
        if (types.includes('postal_code')) det.cp = long_name;
        if (types.includes('country')) det.pais = PAISES[long_name] || long_name;
      });
      setData(prev => ({
        ...prev,
        numeroExt: det.numeroExt || '',
        calle: det.calle || '',
        colonia: det.colonia || '',
        // En CDMX Google da la ciudad, no la alcaldía: se deja vacío para que la capture quien guarda
        municipio: det.municipio === 'Ciudad de México' ? '' : (det.municipio || ''),
        estado: det.estado || '',
        cp: det.cp || '',
        pais: det.pais || '',
        direccion: results[0].formatted_address || '',
        place_id: placeId,
        // Las coordenadas que ya dio Google: el servidor las guarda y el mapa de la página no tiene que volver a preguntar
        location: puntoDeGoogle(results[0].geometry?.location),
      }));
      setErrores({});
    } catch (err) {
      console.error('Error al obtener los datos de la dirección:', err);
    }
  };

  const validar = () => {
    const nuevos = {};
    REQUERIDOS.forEach(campo => {
      if (isEmpty(data[campo])) nuevos[campo] = 'Este campo es requerido';
    });
    if (!/^\d{5}$/.test(data.cp.trim())) nuevos.cp = 'El código postal debe tener 5 dígitos';
    setErrores(nuevos);
    return Object.keys(nuevos).length === 0;
  };

  const guardar = async () => {
    if (!validar()) return;
    setGuardando(true);
    try {
      const antes = new Set((store.getState().profile.profile.direcciones || []).map(d => d._id));
      const ok = await dispatch(addProfileAddress({
        nombre: data.nombre.trim(),
        direccion: data.direccion
          || `${data.calle} ${data.numeroExt}, ${data.colonia}, ${data.municipio}, ${data.estado}, ${data.cp}, ${data.pais}`,
        calle: data.calle,
        numeroInt: data.numeroInt,
        numeroExt: data.numeroExt,
        colonia: data.colonia,
        municipio: data.municipio,
        estado: data.estado,
        cp: data.cp,
        pais: data.pais,
        referencia: data.referencia,
        place_id: data.place_id,
        location: data.location,
      }));
      // La acción actualiza el perfil en Redux: la dirección nueva es la que antes no estaba
      const nueva = ok && (store.getState().profile.profile.direcciones || []).find(d => !antes.has(d._id));
      if (!nueva) {
        setErrores({ general: 'No se pudo guardar la dirección. Revisa los datos e intenta de nuevo.' });
        return;
      }
      onSaved(nueva);
      onClose();
    } catch (err) {
      console.error('Error al guardar la dirección:', err);
      setErrores({ general: 'No se pudo guardar la dirección. Revisa tu conexión e intenta de nuevo.' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="titulo-modal-direccion"
    >
      <DialogTitle id="titulo-modal-direccion" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="span" color="primary">Agregar dirección</Typography>
        <IconButton aria-label="Cerrar" onClick={onClose} disabled={guardando}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {errores.general && <Alert severity="error" sx={{ mb: 2 }}>{errores.general}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <InputLabel>Buscar dirección</InputLabel>
            <GooglePlacesAutocomplete
              autocompletionRequest={{
                types: ['address'],
                componentRestrictions: { country: ['mx', 'us'] },
              }}
              selectProps={{
                value: lugar,
                onChange: elegirLugar,
                placeholder: 'Escribe la calle y el número...',
                isClearable: true,
                styles: { menu: (base) => ({ ...base, zIndex: 20 }) },
              }}
            />
            <FormHelperText>Elígela de la lista y se llenan los campos; si no aparece, captúrala abajo.</FormHelperText>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <InputLabel htmlFor="dir-pais" required>País</InputLabel>
            <FormControl fullWidth size="small" error={!!errores.pais}>
              <Select
                id="dir-pais"
                displayEmpty
                value={data.pais}
                onChange={(e) => cambiar('pais', e.target.value)}
                renderValue={(valor) => valor || <em>Selecciona el país</em>}
              >
                <MenuItem value="México">México</MenuItem>
                <MenuItem value="EEUU">EEUU</MenuItem>
              </Select>
              {errores.pais && <FormHelperText>{errores.pais}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Campo
              id="dir-estado" label="Estado" requerido error={errores.estado} placeholder="Estado al que pertenece"
              value={data.estado} onChange={(e) => cambiar('estado', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Campo
              id="dir-municipio" label="Municipio" requerido error={errores.municipio} placeholder="Municipio o alcaldía"
              value={data.municipio} onChange={(e) => cambiar('municipio', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Campo
              id="dir-colonia" label="Colonia" requerido error={errores.colonia} placeholder="Colonia a la que pertenece"
              value={data.colonia} onChange={(e) => cambiar('colonia', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Campo
              id="dir-calle" label="Calle" requerido error={errores.calle} placeholder="Calle"
              value={data.calle} onChange={(e) => cambiar('calle', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 4 }}>
            <Campo
              id="dir-cp" label="C.P" requerido error={errores.cp} placeholder="5 dígitos"
              value={data.cp} onChange={(e) => cambiar('cp', e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 5 } }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Campo
              id="dir-numeroExt" label="Núm. exterior" placeholder="Número exterior"
              value={data.numeroExt} onChange={(e) => cambiar('numeroExt', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Campo
              id="dir-numeroInt" label="Núm. interior" placeholder="Número interior"
              value={data.numeroInt} onChange={(e) => cambiar('numeroInt', e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <Campo
              id="dir-nombre" label="Nombre" requerido error={errores.nombre}
              placeholder="¿A qué corresponde esta dirección? Ejemplo: Casa, Oficina, Taller..."
              value={data.nombre} onChange={(e) => cambiar('nombre', e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <Campo
              id="dir-referencia" label="Referencia" placeholder="Referencia de la dirección" multiline rows={3}
              value={data.referencia} onChange={(e) => cambiar('referencia', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button type="button" variant="outlined" onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={guardar}
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {guardando ? 'Guardando...' : 'Guardar dirección'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ModalDireccion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ModalDireccion;
